import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChapterLabel, Container, CTAButton, FadeUp, MaskedLine, scrollToHash } from "./ui";

const SplitSection = ({ id, chapter, label, lines, accentLine, body, cta, image, imageAlt, reverse = false, dark = false, tags = [], note = null, featured = false }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section
      id={id}
      ref={ref}
      className={`py-24 md:py-36 ${dark ? "bg-surface" : "bg-ink"}`}
      data-testid={`${id}-section`}
    >
      <Container>
        <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16 lg:gap-24">
          <FadeUp className={`relative overflow-hidden ${reverse ? "md:order-2" : ""}`} amount={0.25}>
            <div className={`relative overflow-hidden ${featured ? "h-[420px] md:h-[640px]" : "h-[380px] md:h-[560px]"}`}>
              <motion.img
                src={image}
                alt={imageAlt}
                loading="lazy"
                style={{ y: imgY }}
                className="absolute inset-0 h-[115%] w-full object-cover brightness-[0.85]"
              />
              <div className="absolute inset-0 bg-ink/35" aria-hidden="true" />
            </div>
          </FadeUp>

          <div className={reverse ? "md:order-1" : ""}>
            <ChapterLabel num={chapter} label={label} />
            <h2 className="mt-10 font-serif font-medium leading-[0.95] text-ivory text-[clamp(38px,4.6vw,72px)]">
              {lines.map((l, i) => (
                <MaskedLine key={l} delay={0.1 + i * 0.1} className={i === accentLine ? "text-champagne" : ""}>
                  {l}
                </MaskedLine>
              ))}
            </h2>
            <FadeUp delay={0.25} className="mt-8">
              {body.map((p) => (
                <p key={p.slice(0, 24)} className="mb-4 max-w-[520px] font-sans text-[15px] font-light leading-[1.8] text-neutral-400 md:text-base">
                  {p}
                </p>
              ))}
            </FadeUp>
            {tags.length > 0 && (
              <FadeUp delay={0.3} className="mt-8">
                <div className="flex max-w-[560px] flex-wrap gap-2" data-testid={`${id}-tags`}>
                  {tags.map((t) => (
                    <span
                      key={t}
                      className="rounded-[2px] border border-line-light px-3.5 py-2 font-display text-[10px] font-medium uppercase tracking-[0.14em] text-neutral-400 transition-colors duration-300 hover:border-champagne/50 hover:text-ivory"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </FadeUp>
            )}
            {note && (
              <FadeUp delay={0.35} className="mt-8">
                <p className="max-w-[520px] border-l border-champagne/50 pl-5 font-sans text-[13px] font-light italic leading-[1.7] text-neutral-500" data-testid={`${id}-note`}>
                  {note}
                </p>
              </FadeUp>
            )}
            <FadeUp delay={0.4} className="mt-9">
              <CTAButton variant="secondary" onClick={() => scrollToHash("#demande")} testid={`${id}-cta`}>
                {cta}
              </CTAButton>
            </FadeUp>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default SplitSection;
